import apiClientService from "./ApiClientService";

/**
 * POST /notifications/confirm/{token}
 */
export async function confirmReservationPresence(token) {
   const safe = encodeURIComponent(String(token || ""));
   // NU mai pune /api aici, ApiClientService îl are deja
   const res = await apiClientService.post(`/notifications/confirm/${safe}`);

   if (!res.ok) {
      // încearcă să citești body-ul de eroare (dacă există)
      let msg = "Nu s-a putut confirma prezența.";
      try {
         const err = await res.json();
         if (err?.message) msg = err.message;
      } catch {}
      throw new Error(msg);
   }

   // 201 poate să nu aibă body → protejăm parsarea
   try {
      return await res.json();
   } catch {
      return {};
   }
}

/**
 * POST /notifications/test-tomorrow
 */
export async function testNotificationsTomorrow() {
   const res = await apiClientService.post(`/notifications/test-tomorrow`);

   if (!res.ok) {
      let msg = "Nu s-au putut trimite notificările de test.";
      try {
         const err = await res.json();
         if (err?.message) msg = err.message;
      } catch {}
      throw new Error(msg);
   }

   try {
      return await res.json();
   } catch {
      return {};
   }
}

async function parseJsonSafe(res) {
   try {
      return await res.json();
   } catch {
      return {};
   }
}

async function throwFromResponse(res, fallbackMsg) {
   let msg = fallbackMsg;
   try {
      const err = await res.json();
      if (err?.message) {
         msg = Array.isArray(err.message) ? err.message.join(" ") : err.message;
      }
   } catch {}
   throw new Error(msg);
}

/**
 * Normalizează răspunsul paginat al backend-ului la o formă stabilă.
 * Acceptă array simplu sau obiect cu {items|data|results|rows|notifications}.
 */
export function normalizeNotificationsPage(raw) {
   if (Array.isArray(raw)) {
      return { items: raw, total: raw.length, page: 1, limit: raw.length };
   }
   if (!raw || typeof raw !== "object") {
      return { items: [], total: 0, page: 1, limit: 0 };
   }

   const items =
      raw.items ||
      raw.data ||
      raw.results ||
      raw.rows ||
      raw.notifications ||
      raw.list ||
      [];

   const list = Array.isArray(items) ? items : [];

   const total = Number(
      raw.total ?? raw.count ?? raw.totalCount ?? raw.totalItems ?? list.length,
   );
   const page = Number(raw.page ?? raw.currentPage ?? 1);
   const limit = Number(raw.limit ?? raw.perPage ?? raw.pageSize ?? list.length);

   return {
      items: list,
      total: Number.isFinite(total) ? total : list.length,
      page: Number.isFinite(page) ? page : 1,
      limit: Number.isFinite(limit) ? limit : list.length,
   };
}

// Contorul e montat de două ori (antetul și bara de sus a elevului) și ambele cer deodată (pornire, focus, timer):
// cererile simultane cu aceiași parametri împart un singur răspuns.
const inFlight = new Map();

function shareInFlight(key, run) {
   if (!inFlight.has(key)) {
      inFlight.set(
         key,
         run().finally(() => inFlight.delete(key)),
      );
   }
   return inFlight.get(key);
}

/**
 * GET /notifications — lista notificărilor proprii, paginată.
 */
export async function getNotifications({
   page = 1,
   limit = 20,
   unreadOnly = false,
} = {}) {
   const qs = new URLSearchParams();
   qs.set("page", String(page));
   qs.set("limit", String(limit));
   qs.set("unreadOnly", String(Boolean(unreadOnly)));

   const path = `/notifications?${qs.toString()}`;
   const result = await shareInFlight(path, async () => {
      const res = await apiClientService.get(path);
      if (!res.ok) {
         await throwFromResponse(res, "Nu s-au putut încărca notificările.");
      }
      return normalizeNotificationsPage(await parseJsonSafe(res));
   });
   // fiecare apelant primește lista lui (o pot modifica)
   return { ...result, items: Array.isArray(result?.items) ? [...result.items] : result?.items };
}

/**
 * GET /notifications/sent — notificările trimise (pagina „Notificări” a personalului, Admin / Manager).
 * Filtre: q (destinatarul: nume, telefon, email, id), audience (students | staff | admin | manager | instructor | professor), type,
 * status (read | unread), from / to (Date sau ISO), page, limit.
 * → { items: [{ id, type, title, body, isRead, readAt, createdAt, user }], total, unread, page, limit, totalPages }
 */
export async function getSentNotifications({ q, audience, type, status, from, to, page = 1, limit = 50 } = {}) {
   const qs = new URLSearchParams();
   const put = (key, value) => {
      if (value === undefined || value === null || value === "") return;
      qs.set(key, value instanceof Date ? value.toISOString() : String(value));
   };
   put("q", String(q || "").trim());
   put("audience", audience);
   put("type", type);
   put("status", status);
   put("from", from);
   put("to", to);
   put("page", page);
   put("limit", limit);
   const res = await apiClientService.get(`/notifications/sent?${qs.toString()}`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-au putut încărca notificările trimise.");
   }
   const data = await parseJsonSafe(res);
   return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total) || 0,
      unread: Number(data?.unread) || 0,
      page: Number(data?.page) || page,
      totalPages: Number(data?.totalPages) || 0,
   };
}

/**
 * GET /notifications/unread-count — numărul de notificări necitite.
 */
export function getUnreadNotificationsCount() {
   return shareInFlight("unread-count", fetchUnreadNotificationsCount);
}

async function fetchUnreadNotificationsCount() {
   const res = await apiClientService.get(`/notifications/unread-count`);
   if (!res.ok) {
      await throwFromResponse(
         res,
         "Nu s-a putut obține numărul de notificări necitite.",
      );
   }
   const data = await parseJsonSafe(res);
   if (typeof data === "number") return data;
   const value = Number(
      data?.count ?? data?.unread ?? data?.unreadCount ?? data?.total ?? 0,
   );
   return Number.isFinite(value) ? value : 0;
}

/**
 * PATCH /notifications/{id}/read — marchează o notificare ca citită.
 */
export async function markNotificationRead(id) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.patch(`/notifications/${safe}/read`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut marca notificarea ca citită.");
   }
   return parseJsonSafe(res);
}

/**
 * PATCH /notifications/read-all — marchează toate notificările ca citite.
 */
export async function markAllNotificationsRead() {
   const res = await apiClientService.patch(`/notifications/read-all`);
   if (!res.ok) {
      await throwFromResponse(
         res,
         "Nu s-au putut marca notificările ca citite.",
      );
   }
   return parseJsonSafe(res);
}

/**
 * POST /notifications/document-ready — notifică studentul că un document
 * e gata de ridicare (Admin/Manager).
 */
export async function notifyDocumentReady({ userId, message }) {
   const res = await apiClientService.post(`/notifications/document-ready`, {
      userId: Number(userId),
      message: String(message || ""),
   });
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut trimite notificarea.");
   }
   return parseJsonSafe(res);
}
